import { type UserWorkspacesResponse } from './types';

const BITBUCKET_WORKSPACES_URL = '/gateway/api/bitbucket/user/workspaces?pagelen=1';

let cachedPromise: Promise<UserWorkspacesResponse> | null = null;

/**
 * Fetches the list of Bitbucket workspaces the current user has access to.
 * Uses a relative URL so the request goes to the current origin (e.g. https://pug.jira-dev.com/gateway/api/bitbucket/user/workspaces).
 * Result is cached in memory; subsequent calls return the cached promise.
 */
export function getUserWorkspaces(): Promise<UserWorkspacesResponse> {
	if (cachedPromise) {
		return cachedPromise;
	}
	cachedPromise = fetch(BITBUCKET_WORKSPACES_URL, {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		},
	})
		.then((response) => {
			if (response.ok) {
				return response.json();
			}
			return Promise.reject(new Error(`${response.status} ${response.statusText}`));
		})
		.catch((err) => {
			cachedPromise = null;
			throw err;
		});
	return cachedPromise;
}

/**
 * Extracts Bitbucket workspace UUID from the /user/workspaces API response.
 * Only the first workspace in the list is returned; UUIDs are passed through as returned by the API (no normalisation).
 */
export function getFirstBitbucketWorkspaceId(response: UserWorkspacesResponse): string[] {
	const first = response.values?.[0]?.workspace?.uuid ?? '';
	return first.length > 0 ? [first] : [];
}
